import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { marketplaceAPI, servicesAPI, messagesAPI } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { servicePriceLabel } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import ReviewsSection from "@/components/ReviewsSection";
import {
  ArrowLeft, Globe, MapPin, Wrench, Search, Store, MessageSquare, Trash2, Loader2, ImageOff, FileText,
} from "lucide-react";

export default function ServiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [contactOpen, setContactOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [storeSummary, setStoreSummary] = useState(null);

  useEffect(() => {
    setLoading(true);
    marketplaceAPI.getListing(id)
      .then((r) => {
        setListing(r.data);
        if (r.data?.seller_id) servicesAPI.getStorefrontSummary(r.data.seller_id).then((s) => setStoreSummary(s.data)).catch(() => {});
      })
      .catch(() => setListing(null))
      .finally(() => setLoading(false));
    marketplaceAPI.incrementListingView(id).catch(() => {});
    servicesAPI.getCategories().then((r) => setCats(r.data?.categories || [])).catch(() => {});
  }, [id]);

  const catLabel = (slug) => cats.find((c) => c.slug === slug)?.label || slug;

  if (loading) return <div className="mx-auto max-w-4xl space-y-4"><Skeleton className="h-8 w-40" /><Skeleton className="aspect-video w-full rounded-xl" /></div>;
  if (!listing || listing.listing_kind !== "service") {
    return (
      <div className="mx-auto max-w-4xl py-20 text-center">
        <p className="text-lg font-medium">Service not found</p>
        <Button className="mt-4" onClick={() => navigate("/marketplace?tab=services")}>Back</Button>
      </div>
    );
  }

  const isOwner = user?.id === listing.seller_id;
  const isRequest = listing.service_type === "request";
  const canBuy = !isRequest && listing.pricing_type === "fixed" && Number(listing.price) > 0;
  const img = listing.images?.[activeImg];

  const startContact = async (prefill, intent) => {
    const body = (message || prefill || "").trim();
    if (!body) return toast.error("Write a message");
    setBusy(true);
    try {
      const res = await messagesAPI.startConversation({
        recipient_id: listing.seller_id,
        content: body,
        context_type: "service",
        context_id: listing.id,
        context_title: listing.title,
      });
      toast.success(intent === "quote" ? "Quote request sent" : "Message sent");
      setContactOpen(false);
      setQuoteOpen(false);
      navigate(`/messages/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send message");
    } finally {
      setBusy(false);
    }
  };

  const buyNow = () => navigate(`/checkout/${listing.id}?pickup=true`);

  const remove = async () => {
    if (!window.confirm("Delete this service listing?")) return;
    try {
      await marketplaceAPI.deleteListing(listing.id);
      toast.success("Deleted");
      navigate("/marketplace?tab=services");
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <button onClick={() => navigate("/marketplace?tab=services")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="grid gap-6 md:grid-cols-5">
        <div className="md:col-span-3 space-y-3">
          <div className="relative aspect-video overflow-hidden rounded-xl border border-border bg-secondary">
            {img ? (
              <img src={img} alt={listing.title} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-muted-foreground"><ImageOff className="h-12 w-12" /></div>
            )}
            <Badge className={`absolute left-3 top-3 border-0 text-xs font-bold uppercase text-white ${isRequest ? "bg-amber-600" : "bg-primary"}`}>
              {isRequest ? (<><Search className="mr-1 h-3 w-3" /> Looking for</>) : (<><Wrench className="mr-1 h-3 w-3" /> Offering</>)}
            </Badge>
          </div>
          {listing.images?.length > 1 && (
            <div className="flex gap-2">
              {listing.images.map((im, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`h-16 w-16 overflow-hidden rounded-md border ${i === activeImg ? "border-primary" : "border-border"}`}>
                  <img src={im} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="md:col-span-2 space-y-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{catLabel(listing.category)}</p>
            <h1 className="mt-1 font-display text-2xl font-bold leading-tight" data-testid="service-detail-title">{listing.title}</h1>
          </div>
          <div className="text-2xl font-bold text-primary" data-testid="service-detail-price">{servicePriceLabel(listing)}</div>
          <div className="flex flex-wrap gap-2 text-xs">
            {listing.remote_ok && <span className="flex items-center gap-1 rounded bg-secondary px-2 py-1"><Globe className="h-3 w-3" /> Remote OK</span>}
            {listing.location && <span className="flex items-center gap-1 rounded bg-secondary px-2 py-1"><MapPin className="h-3 w-3" /> {listing.location}</span>}
          </div>

          <Link to={`/storefront/${listing.seller_id}`} className="flex items-center gap-2 rounded-lg border-2 border-primary/60 bg-primary/5 p-3 transition-colors hover:border-primary hover:bg-primary/10" data-testid="service-detail-storefront-link">
            {storeSummary?.profile_image ? (
              <img src={storeSummary.profile_image} alt="" className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <Store className="h-5 w-5 text-primary" />
            )}
            <div className="flex-1">
              <p className="text-sm font-semibold">{storeSummary?.brand_name || listing.seller_username}</p>
              <p className="text-xs font-medium text-primary">View storefront →</p>
            </div>
          </Link>

          {isOwner ? (
            <Button variant="destructive" onClick={remove} className="w-full" data-testid="service-delete-button">
              <Trash2 className="mr-2 h-4 w-4" /> Delete listing
            </Button>
          ) : (
            <div className="space-y-2">
              {canBuy && (
                <Button onClick={buyNow} className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="service-buy-button">
                  Buy now · {servicePriceLabel(listing)}
                </Button>
              )}
              {!isRequest && (
                <Button variant="outline" onClick={() => { setMessage(""); setQuoteOpen(true); }} className="w-full" data-testid="service-request-quote-button">
                  <FileText className="mr-2 h-4 w-4" /> Request quote
                </Button>
              )}
              <Button variant="outline" onClick={() => { setMessage(""); setContactOpen(true); }} className="w-full" data-testid="service-contact-button">
                <MessageSquare className="mr-2 h-4 w-4" /> {isRequest ? "Offer to help" : "Contact"}
              </Button>
            </div>
          )}
        </div>
      </div>

      <Card className="border-border bg-card p-5">
        <h2 className="font-display text-lg font-bold">Details</h2>
        <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-foreground/80">{listing.description}</p>
      </Card>

      <ReviewsSection sellerId={listing.seller_id} kind="service" />

      {/* Contact dialog */}
      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{isRequest ? "Offer to help" : `Message ${listing.seller_username}`}</DialogTitle></DialogHeader>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder="Introduce yourself and your request..." data-testid="service-contact-message" />
          <DialogFooter>
            <Button onClick={() => startContact(null, "contact")} disabled={busy} className="bg-primary text-primary-foreground" data-testid="service-contact-send">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request quote dialog */}
      <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Request a quote</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Tell {listing.seller_username} what you need. They'll reply with a price you can pay securely here.</p>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder={`e.g. I need ${listing.title.toLowerCase()} for my GT3 on iRacing...`} data-testid="service-quote-message" />
          <DialogFooter>
            <Button onClick={() => startContact(`I'd like to request a quote for "${listing.title}".`, "quote")} disabled={busy} className="bg-primary text-primary-foreground" data-testid="service-quote-send">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
